import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Імпортуємо локалізації тільки для швидкого початкового рендерингу
// Повні локалізації будуть завантажені через HTTP
import translationUK from './locales/uk/translation.json';
import translationEN from './locales/en/translation.json';
import translationDE from './locales/de/translation.json';
import translationLT from './locales/lt/translation.json';
import privacyEN from './locales/policies/en/privacy.json';
import privacyUK from './locales/policies/uk/privacy.json';
import privacyDE from './locales/policies/de/privacy.json';
import privacyLT from './locales/policies/lt/privacy.json';
import termsEN from './locales/policies/en/terms.json';
import termsUK from './locales/policies/uk/terms.json';
import termsDE from './locales/policies/de/terms.json';
import termsLT from './locales/policies/lt/terms.json';
import accountDeletionUK from './locales/uk/account-deletion.json';
import accountDeletionEN from './locales/en/account-deletion.json';

// Базові ресурси перекладу (включаючи політику конфіденційності та умови використання)
const resources = {
  uk: { 
    translation: translationUK,
    privacy: privacyUK,
    terms: termsUK,
    'account-deletion': accountDeletionUK
  },
  en: { 
    translation: translationEN,
    privacy: privacyEN,
    terms: termsEN,
    'account-deletion': accountDeletionEN
  },
  de: { 
    translation: translationDE,
    privacy: privacyDE,
    terms: termsDE
  },
  lt: { 
    translation: translationLT,
    privacy: privacyLT,
    terms: termsLT
  },
};

// Доступні мови
export const languages = ['uk', 'en', 'de', 'lt'];

// Визначення мови з URL шляху
export const getLanguageFromPath = (path) => {
  if (!path) return null;
  const pathSegments = path.split('/').filter(segment => segment);
  const firstSegment = pathSegments[0];
  
  if (languages.includes(firstSegment)) {
    return firstSegment;
  }
  
  return null;
};

// Отримання шляху без мовного префікса
export const getPathWithoutLanguage = (path) => {
  if (!path) return '/';
  const pathSegments = path.split('/').filter(segment => segment);
  const firstSegment = pathSegments[0];
  
  if (languages.includes(firstSegment)) {
    return '/' + pathSegments.slice(1).join('/');
  }
  
  return path;
};

// Створення URL з мовним префіксом
export const getLocalizedPath = (path, language) => {
  const pathWithoutLang = getPathWithoutLanguage(path);
  return `/${language}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
};

// Функція для визначення найкращої мови із наявних
export const getBestMatchLanguage = () => {
  // Спроба отримати мову з localStorage
  const savedLang = localStorage.getItem('i18nextLng');
  if (savedLang && languages.includes(savedLang)) {
    return savedLang;
  }
  
  // Спроба отримати мову браузера
  const browserLang = (navigator.language || navigator.userLanguage).split('-')[0].toLowerCase();
  if (languages.includes(browserLang)) {
    return browserLang;
  }
  
  // За замовчуванням англійська
  return 'en';
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Використовуємо вбудовані ресурси для швидкого рендерингу
    resources,
    
    // Стандартні налаштування
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    // Простори імен для перекладів
    ns: ['translation', 'privacy', 'terms', 'account-deletion'],
    defaultNS: 'translation',
    
    // Налаштування backend для HTTP запитів
    backend: {
      loadPath: (lngs, namespaces) => {
        const lng = lngs[0];
        const ns = namespaces[0];
        if (ns === 'privacy' || ns === 'terms') {
          return `/locales/policies/${lng}/${ns}.json`;
        }
        return `/locales/${lng}/${ns}.json`;
      },
      
      // Додаткові налаштування для запитів
      requestOptions: {
        cache: 'no-store',
        mode: 'cors'
      },
      
      // Міжсайтові запити
      crossDomain: true
    },
    
    // Інші налаштування
    interpolation: {
      escapeValue: false, // не потрібно для React
    },
    
    // Налаштування визначення мови
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      caches: ['localStorage'],
      checkWhitelist: true,
    },
    
    // Використання вбудованих ресурсів як резервних, якщо HTTP запит не працює
    partialBundledLanguages: true,
  });

// Оновлюємо атрибут lang у HTML-елементі при зміні мови
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

// Встановлюємо початкову мову
document.documentElement.lang = i18n.language;

export default i18n; 